import mailcheck from 'mailcheck';
import React, { FormEvent, InputHTMLAttributes, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EMAIL_PLACEHOLDER_ANIM_START_DELAY } from 'SharedComponents/EmailInput';
import { ResponseError } from 'SharedHelpers/LoginRegisterFormAPI';
import StepOne from 'SharedComponents/StepOne';
import Steps from 'SharedComponents/Steps';
import StepTwo from 'SharedComponents/StepTwo';
import { SignupFormProps } from 'Interfaces/LoginRegisterFormProps';
import SwitchLink from 'SharedComponents/SwitchLink';
import { FormFieldTracking } from 'Interfaces/overlayTracking';
import { FormWrapper } from 'SharedComponents/styled/forms/FormWrapper';
import useTranslate from 'Hooks/useTranslate';
import useSignupFormTracking from 'Hooks/useSignupFormTracking';
import useLocationChange from 'Hooks/useLocationChange';
import useTimeout from 'SharedHooks/useTimeout';
import ActionLink from 'SharedComponents/ActionLink';
import { addUrlParam } from 'SharedHelpers/url';
import { OnFailureData } from 'Interfaces/Api';
import { useErrorHandler } from 'Hooks/useErrorHandler';
import HiddenFields from 'SharedComponents/HiddenFields';
import useApi from 'Hooks/useApi';

const SignupForm: React.FunctionComponent<SignupFormProps> = formProps => {
    const props = {
        mailcheckOptions: {},
        animateEmailPlaceholder: false,
        ...formProps,
    };

    const {
        setActiveStep,
        setIsLastStep,
        isLastStep,
        isMultiStep,
        setIsLoading,
        redirectUrl,
        setIsFolded,
        isFolded,
        handleClose,
        activeStep,
    } = props;

    const { registerUser } = useApi();
    const [isEmailInputFocus, setIsEmailInputFocus] = useState(false);
    const [playEmailPlaceholderAnim, setPlayEmailPlaceholderAnim] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(true);
    const { errorHandler } = useErrorHandler();

    const { tPlain, tt } = useTranslate();
    const { locationChange } = useLocationChange();
    const { trackFieldChecked, trackFieldFilled, trackFieldError, trackFormSubmit } = useSignupFormTracking();

    const signupFormApi = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        criteriaMode: 'all',
    });

    useTimeout(
        () =>
            setPlayEmailPlaceholderAnim(
                !props.inputEmail && props.animateEmailPlaceholder && !isEmailInputFocus && isFolded
            ),
        EMAIL_PLACEHOLDER_ANIM_START_DELAY * 1000
    );

    const { register, handleSubmit, reset, setError, clearErrors, errors, getValues, setValue, trigger, watch } =
        signupFormApi;

    const signupFieldsWatcher = watch(['email', 'password', 'agb']);

    useEffect(() => {
        if (signupFieldsWatcher.agb === '1') {
            trackFieldChecked(FormFieldTracking.AGBFormField);
        }
        if (signupFieldsWatcher.email?.length > 0) {
            trackFieldFilled(FormFieldTracking.EmailFormField);
        }
        if (signupFieldsWatcher.password?.length > 0) {
            trackFieldFilled(FormFieldTracking.PasswordFormField);
        }
    }, [signupFieldsWatcher, trackFieldChecked, trackFieldFilled]);

    useEffect(() => {
        if (errors.email) {
            trackFieldError(FormFieldTracking.EmailFormField);
        }
        if (errors.password) {
            trackFieldError(FormFieldTracking.PasswordFormField);
        }
    }, [errors.email, errors.password, trackFieldError]);

    useEffect(
        () => () => {
            reset();

            setActiveStep(1);
            setIsLastStep(false);
        },
        [setActiveStep, setIsLastStep, reset]
    );

    const handleSwitch = () => {
        props.handleShowLoginToggle();
        // TODO Club
        // removeQueryStrKey('signup');
        // updateQueryStr('login', 1);
        clearErrors();
    };

    const handleEmailChange = () => {
        props.handleEmailChange(getValues('email'));
        clearErrors('email');
    };

    const checkEmailDomainSpelling = () => {
        // TODO Club add options
        const suggestion = mailcheck.run({ email: getValues('email') });
        if (!suggestion) {
            return;
        }

        const message = tt`Did you mean ${(
            <ActionLink
                type="button"
                onClick={() => {
                    setValue('email', suggestion?.full);
                    clearErrors('email');
                }}
                className="qa-login-signup-form__email-autocorrect-error"
            >
                {suggestion.full}
            </ActionLink>
        )}?` as unknown as string;

        // TODO implement tt
        setError('email', {
            type: 'emailSuggestion',
            message,
        });
    };

    const onSubmit = useCallback(
        async formInput => {
            trackFormSubmit();
            setIsLoading(true);

            await registerUser(
                formInput,
                () => {
                    handleClose();
                    if (redirectUrl) {
                        locationChange(redirectUrl);
                    }
                },
                (signupErrors: OnFailureData<ResponseError>) => {
                    const callbacks = { setError, setIsLoading };
                    const submitInfo = { isLastStep, isMultiStep, redirectUrl, showLogin: false };
                    errorHandler(submitInfo, signupErrors, callbacks);
                }
            );
        },
        [
            trackFormSubmit,
            registerUser,
            setIsLoading,
            handleClose,
            redirectUrl,
            locationChange,
            errorHandler,
            isLastStep,
            isMultiStep,
            setError,
        ]
    );

    const isFirstStepFormValid = async (): Promise<boolean> => {
        const passwordValidationResult = await trigger('password');

        if (!passwordValidationResult) {
            setError('password', {
                type: 'custom',
                message: tPlain('signupOverlay_Invalid password'),
            });
            return false;
        }

        const emailValidationResult = await props.backendEmailValidator(getValues());
        if (emailValidationResult !== true) {
            setError('email', {
                type: 'custom',
                message: emailValidationResult,
            });
            return false;
        }

        return true;
    };

    const handleSubmitProxy = async (event: FormEvent) => {
        event.preventDefault();

        if (isFolded) {
            setIsFolded(false);
            return;
        }

        if (isMultiStep && activeStep === 1) {
            const isValid = await isFirstStepFormValid();

            if (!isValid) {
                return;
            }

            if (!isLastStep) {
                props.setActiveStep(activeStep + 1);
                trackFormSubmit(false);
            }
        }

        if (!isMultiStep || isLastStep) {
            await handleSubmit(onSubmit)();
        }
    };

    /**
     * For autofilled email input we expand the form
     */
    useEffect(() => {
        const autofillTimer = setInterval(() => {
            const emailValue = getValues('email');
            if (emailValue) {
                setIsFolded(false);
                clearInterval(autofillTimer);
            }
        }, 500);
        const clearTimer = setTimeout(() => clearInterval(autofillTimer), 2000);
        return () => {
            clearInterval(autofillTimer);
            clearTimeout(clearTimer);
        };
    }, [getValues, setIsFolded]);

    const emailInputProps: InputHTMLAttributes<HTMLElement> = {
        id: props.formIdentifiers?.email,
        defaultValue: props.inputEmail,
        onBlur: () => {
            checkEmailDomainSpelling();
            setIsEmailInputFocus(false);
        },
        onFocus: () => {
            setIsEmailInputFocus(true);
            setPlayEmailPlaceholderAnim(false);
        },
        onChange: handleEmailChange,
        onKeyUp: isFolded ? () => setIsFolded(false) : undefined,
    };

    const trackEmailError = () => {
        trackFieldError(FormFieldTracking.EmailFormField);
    };

    const passwordInputProps: InputHTMLAttributes<HTMLElement> = {
        id: props.formIdentifiers?.password,
    };

    const getRedirectUrl = useCallback(
        () => (isSubscribed ? redirectUrl : addUrlParam(redirectUrl, 'newsletter-welcome', '1')),
        [isSubscribed, redirectUrl]
    );

    return (
        <FormWrapper name={props.formName} onSubmit={handleSubmitProxy} className="qa-signup-form gtm-signupForm">
            <HiddenFields
                usingRef={() => register()}
                redirectUrl={getRedirectUrl()}
                isLogin={false}
                isReferral={!!props.isReferral}
            />
            <Steps
                formName={props.formIdentifiers?.submit}
                isLoading={props.isLoading}
                activeStep={props.activeStep}
                setIsLastStep={props.setIsLastStep}
                animatedBtnSubmitCta={false}
                stepKeys={['StepOne', 'StepTwo']}
            >
                <StepOne
                    isFolded={isFolded}
                    setIsFolded={setIsFolded}
                    isFormReady={props.isFormReady}
                    isLoading={props.isLoading}
                    emailInputProps={emailInputProps}
                    backendEmailValidator={props.backendEmailValidator}
                    passwordInputProps={passwordInputProps}
                    playEmailPlaceholderAnim={playEmailPlaceholderAnim}
                    formApi={signupFormApi}
                    showTerms={!props.isMultiStep}
                    errorCallback={trackEmailError}
                />
                {props.isMultiStep && (
                    <StepTwo
                        isLoading={props.isLoading}
                        formApi={signupFormApi}
                        privacyPolicy={props.privacyPolicy}
                        setIsSubscribed={setIsSubscribed}
                    />
                )}
            </Steps>
            {!props.hideSwitch && (
                <SwitchLink
                    className="qa-login-signup-form__switch"
                    onClick={handleSwitch}
                    isLoading={props.isLoading}
                    introText={tPlain('signupOverlay_Already registered?')}
                    ctaText={tPlain('signupOverlay_Switch to login')}
                />
            )}
        </FormWrapper>
    );
};

export default SignupForm;
